import React, { FC, SyntheticEvent, useState } from 'react';

import useIsMobile from '../../../../hooks/useIsMobile';

import { SearchListProps } from './SearchList';
import TabSearchGroup from './TabSearchGroup';
import TabSearchHeader from './TabSearchHeader';
import * as Styled from './styled';
import { SearchData, SelectedItemsType } from './types';

type ListProps = SearchListProps & {
  itemsSelected: SearchData;
};

export type Props = {
  dataSearchValue: string;
  onSearch: (term: string) => void;
  placeholder: string;
  sections: [ListProps, ListProps?];
};

/**
 * Search panel inside the compose form tabs with search and multiple checkbox selection
 *
 * @param props                 Props passed to the component
 * @param props.dataSearchValue <input type="search" /> value attribute
 * @param props.onSearch        Callback to invoke when search term is changed
 * @param props.placeholder     Placeholder for the search input
 * @param props.sections        Lists of items to display (members, groups, labels)
 * @returns                     The component itself
 */
const TabSearch: FC<Props> = ({
  dataSearchValue,
  onSearch,
  placeholder,
  sections,
}) => {
  const isMobile = useIsMobile();

  const [shouldFilterSelected, setShouldFilterSelected] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  /**
   * Handle switching between tabs
   *
   * @param _event   Change event that took place
   * @param newValue Index of tab that was selected
   */
  const handleChange = (_event: SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const selectedLists = sections
    .filter((item): item is ListProps => Boolean(item))
    .map(({ itemsSelected, onChange }: ListProps) => ({
      items: itemsSelected,
      updater: onChange,
    })) as [SelectedItemsType, SelectedItemsType?];

  const tabs = [
    sections[0].type,
    ...(sections[1]?.type === undefined ? [] : [sections[1].type]),
  ];

  const filteredSections = isMobile ? [sections[tabIndex]] : sections;

  return (
    <Styled.Wrapper>
      <TabSearchHeader
        dataSearchValue={dataSearchValue}
        handleChangeTab={handleChange}
        onSearch={onSearch}
        placeholder={placeholder}
        selectedItems={selectedLists}
        setShouldFilterSelected={setShouldFilterSelected}
        shouldFilterSelected={shouldFilterSelected}
        tabIndex={tabIndex}
        tabs={tabs}
      />
      <Styled.SectionsWrapper>
        {filteredSections
          .map(
            listProps =>
              listProps !== undefined && (
                <TabSearchGroup
                  isFilterActive={shouldFilterSelected}
                  key={listProps.type}
                  {...listProps}
                />
              ),
          )
          .filter(Boolean)}
      </Styled.SectionsWrapper>
    </Styled.Wrapper>
  );
};

export default TabSearch;
